import React, { useCallback, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '../../Components/UI/DataTable';
import RowMenu from '../../Components/UI/RowMenu';
import Button from '../../Components/UI/Button';
import Modal from '../../Components/UI/Modal';
import useTableData from '../../Components/CommonComponents/useTableData';
import { apiGet, apiDelete } from '../../Components/CommonComponents/tableApi';
import { getHeadings } from '../OtherFunctions/table';
import { useGetLoggedInUser } from '../../UtilityHookFunctions/LoggedInUser';
import PrescriptionFlagsTable from './PrescriptionFlagsTable';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';
import fetchAllPages, { formatExportDate } from '../../UtilityHookFunctions/fetchAllPages';

const DIAGNOSIS_KEYS = ['primary', 'secondary', 'third'];

const PRESCRIPTION_TABS = [
  { id: 'prescription', label: 'Prescription' },
  { id: 'reports', label: 'Before/After · Case Studies · Reviews · Courier' },
];

const TITLES = {
  patients: 'Patient',
  doctors: 'Doctor',
  receptionist: 'Receptionist',
  consultant: 'Consultant',
  enquiry: 'Enquiry',
  assistantDoctor: 'Assistant Doctor',
  nutrition: 'nutrition',
  feedback: 'feedback',
  prescription: 'prescription',
  Bids: 'Bids',
  bids: 'Bids',
  AlternativeTherapies: 'Alternative Therapies',
  attendancecount: 'Mark Attendance',
};

const getTitle = (listType) =>
  TITLES[listType] || (listType ? listType.charAt(0).toUpperCase() + listType.slice(1) : 'Default Title');

// Base paths per list. Each already ends in `?` or `&&` so the paging suffix
// appends exactly the way it always did.
const ENDPOINTS = {
  patients: 'customer/list/dashboard?',
  doctors: 'doctor/?',
  receptionist: 'receptionist/?',
  consultant: 'consultant/list/dashboard?',
  enquiry: 'enquiry/?',
  assistantDoctor: 'assistantDoctor/list/dashboard?',
  nutrition: 'nutrition/?',
  feedback: 'feedback/?',
  prescription: 'customer/list/dashboard?',
  AlternativeTherapies: 'alternative_therapy/list?search=true&&status=requested&&',
  attendancecount: 'attendance/?',
};

// Every list whose rows can be removed. The staff lists map onto the same
// `DELETE /<role>/:id` routes the profile screens already use.
const DELETE_PATHS = {
  patients: (id) => `customer/${id}`,
  consultant: (id) => `consultant/${id}`,
  doctors: (id) => `doctor/${id}`,
  receptionist: (id) => `receptionist/${id}`,
  assistantDoctor: (id) => `assistantDoctor/${id}`,
};

const EXPORT_DATE_KEYS = ['createdAt', 'date', 'FeedbackDate', 'dateOfBirth', 'updatedAt'];

const withPaging = (base, query) => `${base}page=${query.page}&&limit=${query.limit}&&search=${query.search}`;

export default function Table() {
  const user = useGetLoggedInUser();
  const { listType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Row queued for deletion; the modal is open whenever this is set.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pageTab, setPageTab] = useState('prescription');
  const [isExporting, setIsExporting] = useState(false);

  const { main: baseKeys = [], display: baseLabels = [] } = getHeadings(listType, user) || {};
  // Enquiries record which page/form they came from.
  const withSource = listType === 'enquiry' && !baseKeys.includes('sourcePage');
  const tableKeys = withSource ? [...baseKeys, 'sourcePage'] : baseKeys;
  const headLabels = withSource ? [...baseLabels, 'Source Page'] : baseLabels;

  const fetcher = useCallback(
    (query) => {
      if (listType === 'Bids') {
        const base = user?.role === 'MainDoctor' ? 'bid/?' : 'alternative_therapy/list?search=true&&status=bid&&';
        return apiGet(withPaging(base, query));
      }
      const base = ENDPOINTS[listType];
      return base ? apiGet(withPaging(base, query)) : [];
    },
    [listType, user?.role]
  );

  const table = useTableData(fetcher, [listType, user?.role]);

  const excludedUrls = ['/feedback', '/someotherurl', '/prescription', '/AlternativeTherapies', '/Bids'];
  const canModify = !excludedUrls.includes(location.pathname);

  const headers = tableKeys.map((key, index) => ({
    key,
    label: headLabels?.[index] ?? key,
    render: (value, row) => (DIAGNOSIS_KEYS.includes(key) ? row?.diagnosis?.[key] : value || row?.bidder?.name),
  }));

  // Exports every record matching the current search, not just the visible page.
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const rows = await fetchAllPages((page, limit) =>
        fetcher({ ...table.appliedQuery, page, limit })
      );
      if (!rows.length) {
        toast.info('No records to export');
        return;
      }
      const columns = tableKeys.map((key, index) => ({
        key,
        label: headLabels?.[index] ?? key,
        format: (value, row) => {
          if (DIAGNOSIS_KEYS.includes(key)) return row?.diagnosis?.[key] ?? '';
          if (key === '[bidder][name]') return row?.bidder?.name ?? '';
          if (EXPORT_DATE_KEYS.includes(key)) return formatExportDate(value);
          if (value && typeof value === 'object') return JSON.stringify(value);
          return value ?? '';
        },
      }));
      exportToExcel(rows, `${getTitle(listType)}_list`, columns, getTitle(listType));
    } catch (error) {
      console.log('Export failed', error);
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const openRecord = (item) => {
    if (listType === 'prescription') return navigate(`/${listType}/add/${item._id}`);
    if (listType === 'Bids' && user?.role === 'MainDoctor') return navigate(`/${listType}/review/${item._id}`);
    return navigate(`/${listType}/${item._id}`);
  };

  const menuItems = (item) => [
    {
      label: 'Profile',
      hidden: listType !== 'patients',
      onClick: () => navigate(`/patients/${item._id}/profile`),
    },
    {
      label: 'Prescription',
      hidden: listType !== 'patients',
      onClick: () => navigate(`/prescription/add/${item._id}`),
    },
    {
      label: 'Appointment',
      hidden: listType !== 'patients',
      onClick: () =>
        navigate(`/appointment?patientId=${item._id}`, {
          state: { patientId: item._id, patientData: item },
        }),
    },
    {
      label: 'Copy link',
      hidden: listType !== 'patients',
      onClick: () => {
        navigator.clipboard.writeText(
          `https://admin.adityahomoeopathicclinic.com/public/patient/update/${item._id}`
        );
        toast.success('Link copied');
      },
    },
    {
      label: 'Place bid',
      hidden: !(listType === 'Bids' && user?.role !== 'MainDoctor'),
      onClick: () => navigate(`/${listType}/${item._id}/create`),
    },
    {
      label: 'Delete',
      danger: true,
      divider: true,
      hidden: !canModify || !DELETE_PATHS[listType],
      onClick: () => setPendingDelete(item),
    },
  ];

  const confirmDelete = async () => {
    const item = pendingDelete;
    setPendingDelete(null);
    if (!item || !DELETE_PATHS[listType]) return;
    await apiDelete(DELETE_PATHS[listType](item._id), getTitle(listType), { onDone: table.reload });
  };

  const showTabs = listType === 'prescription';

  return (
    <div className="w-full p-4 md:p-6">
      {showTabs && (
        <div className="mb-4 flex gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 w-fit">
          {PRESCRIPTION_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setPageTab(t.id)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition whitespace-nowrap ${
                pageTab === t.id
                  ? 'bg-white text-sky-700 shadow-sm border border-slate-200'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {showTabs && pageTab === 'reports' ? (
        <PrescriptionFlagsTable />
      ) : (
      <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">{getTitle(listType)}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {tableKeys.length > 0 && (
            <Button variant="outline" onClick={handleExport} isLoading={isExporting}>
              Export to Excel
            </Button>
          )}
          {listType === 'consultant' && (
            <Button variant="outline" onClick={() => navigate('Bids')}>
              View Bids
            </Button>
          )}
          {canModify && <Button onClick={() => navigate('addNew')}>+ Add {getTitle(listType)}</Button>}
        </div>
      </div>

      <DataTable
        headers={headers}
        data={table.rows}
        isLoading={table.isLoading}
        emptyMessage={`No ${getTitle(listType).toLowerCase()} records found.`}
        page={table.query.page}
        totalPages={table.totalPages}
        rowsPerPage={table.query.limit}
        onPageChange={table.setPage}
        onRowsPerPageChange={table.setLimit}
        onSearch={table.setSearch}
        searchValue={table.query.search}
        searchPlaceholder={`Search ${getTitle(listType).toLowerCase()}...`}
        actions={(item) => (
          <>
            <button
              type="button"
              onClick={() => openRecord(item)}
              className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              {canModify ? 'Modify' : 'View'}
            </button>
            <RowMenu items={menuItems(item)} />
          </>
        )}
      />

      <Modal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        title={`Delete ${getTitle(listType)}`}
        subtitle="This cannot be undone"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          Permanently delete{' '}
          <span className="font-semibold text-slate-900">
            {pendingDelete?.name || pendingDelete?.email || 'this record'}
          </span>
          ? Their login access is removed along with the record.
        </p>
      </Modal>

      <ToastContainer />
      </>
      )}
    </div>
  );
}
